import { useIntl } from "react-intl";
import styles from "./MaterialMetaData.module.scss";
import MaterialTags from "../common/MaterialTags";
import { ReactNode } from "react";
import { CourseMaterial } from "./coursesLoader";
import { clsx } from "clsx";
import { Book } from "common/api/decimal/methods/course";
import IsbnInfo from "../../common/product/IsbnInfo.tsx";
import useVirtualStore from "../../common/virtual-store/useVirtualStore.tsx";
import useAccessDuration from "./useAccessDuration.tsx";
import LicenseDuration from "./common/LicenseDuration.tsx";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton.tsx";

interface TitleAction {
  onClick?: () => void | Promise<void>;
  href?: string;
  to?: string;
  ariaLabel?: string;
}

interface MaterialMetaDataProps {
  children?: ReactNode;
  showAccessDuration?: boolean;
  showTags?: boolean;
  size?: "small";
  courseMaterial: CourseMaterial | Book;
  titleTag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div";
  titleAction?: TitleAction;
}

type TagType = "CoachMe" | "Ancillaries";

function Tags({ type }: { type: TagType }) {
  const intl = useIntl();

  const tagConfig = {
    CoachMe: {
      testId: "coachmeTags",
      iconClass: "vst-feature-tag-type-coachme",
      label: intl.formatMessage({ defaultMessage: "CoachMe Included" }),
    },
    Ancillaries: {
      testId: "instructionalTags",
      iconClass: "vst-feature-tag-type-ancillaries",
      label: intl.formatMessage({ defaultMessage: "Instructions" }),
    },
  };

  const { testId, iconClass, label } = tagConfig[type];

  return (
    <div
      data-testid={testId}
      className={clsx(
        "vst-feature-tag",
        "vst-tag-option-ellipsis",
        iconClass,
        styles.tag
      )}
    >
      {label}
    </div>
  );
}

export default function MaterialMetaData({
  children,
  showAccessDuration = true,
  showTags = false,
  size,
  courseMaterial,
  titleTag: TitleTag = "div",
  titleAction,
}: MaterialMetaDataProps) {
  const { isVirtualStore } = useVirtualStore();
  const accessDuration = useAccessDuration(courseMaterial);
  const { coachMeIncluded } = courseMaterial;

  return (
    <div className={clsx(size === "small" && styles.small)}>
      <div className={styles.infoTopRow}>
        {courseMaterial.materialFormat && (
          <div>
            <MaterialTags
              format={courseMaterial.materialFormat}
              buttonLinks={courseMaterial.buttonLinks}
            />
          </div>
        )}
      </div>

      <TitleTag className={styles.title}>
        {titleAction ? (
          <BaseLinkOrButton
            onClick={() => void titleAction.onClick?.()}
            href={titleAction.href}
            to={titleAction.to}
            aria-label={titleAction.ariaLabel}
            className={styles.titleLink}
          >
            {courseMaterial.title}
          </BaseLinkOrButton>
        ) : (
          courseMaterial.title
        )}
      </TitleTag>
      {courseMaterial.author && (
        <div className={styles.metadata}>{courseMaterial.author}</div>
      )}
      <div className={styles.metadata}>
        <IsbnInfo
          identifier={
            isVirtualStore ? courseMaterial.isbn : courseMaterial.vbid
          }
          specifyPrint={false}
        />
      </div>
      {showAccessDuration && accessDuration && (
        <div className={styles.metadata}>
          <LicenseDuration courseMaterial={courseMaterial} />
        </div>
      )}

      {children}

      {showTags && coachMeIncluded && (
        <div className={styles.tags}>
          {coachMeIncluded && <Tags type="CoachMe" />}
        </div>
      )}
    </div>
  );
}
